// src/services/CourseStructureService.js
import authService from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL;


// ============================
// Helpers
// ============================

// Manejo de respuestas (ahora soporta 204 y no-JSON)
export const handleResponse = async (response) => {
  if (!response.ok) {
    if (response.status === 401) {
      // Si el wrapper ya refrescó y aún es 401, forzamos logout
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      window.location.href = "/login";
      throw new Error("Token expirado o inválido");
    }
    if (response.status === 403) throw new Error("No tienes permisos para esta acción");
    if (response.status === 404) throw new Error("Recurso no encontrado");

    // Intenta leer JSON o texto de error
    const raw = await response.text().catch(() => "");
    try {
      const j = raw ? JSON.parse(raw) : {};
      throw new Error(j?.message || j?.error || j?.details || `Error en la petición: ${response.status}`);
    } catch {
      throw new Error(raw || `Error en la petición: ${response.status}`);
    }
  }

  if (response.status === 204) return null;
  const ct = response.headers.get("content-type") || "";
  return ct.includes("application/json") ? response.json() : response.text();
};

// Peticiones autenticadas (usa el wrapper que refresca token y reintenta 1 vez)
const authenticatedRequest = async (url, options = {}) => {
  const res = await authService.authenticatedRequest(url, options);
  return await handleResponse(res);
};

class CourseStructureService {
  // ============================
  // Utilidades
  // ============================
  static toAbsoluteUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("blob:")) return url;
    return `${API_BASE_URL}${url}`;
  };

  // ============================
  // Básicas
  // ============================
  static async getCourse(courseId) {

    return authenticatedRequest(`/api/admin/courses/${courseId}`);
  }

  static async getCourseStructure(courseId) {

    const data = await authenticatedRequest(`/api/admin/courses/${courseId}/structure`);
    return data?.structure || [];
  }

  static async createStructureItem(itemData) {

    return authenticatedRequest(`/api/admin/courses/${itemData.curso_id}/structure`, {
      method: "POST",
      body: JSON.stringify(itemData),
    });
  }

  static async updateStructureItem(itemId, itemData) {

    return authenticatedRequest(`/api/admin/courses/structure/${itemId}`, {
      method: "PUT",
      body: JSON.stringify(itemData),
    });
  }

  static async deleteStructureItem(itemId) {

    return authenticatedRequest(`/api/admin/courses/structure/${itemId}`, {
      method: "DELETE",
    });
  }

  static async reorderStructureItems(draggedItemId, targetItemId) {

    return authenticatedRequest(`/api/admin/courses/structure/reorder`, {
      method: "POST",
      body: JSON.stringify({ draggedItemId, targetItemId }),
    });
  }

  // ============================
  // Avanzadas
  // ============================
  static async duplicateStructureItem(itemId) {

    return authenticatedRequest(`/api/admin/courses/structure/${itemId}/duplicate`, { method: "POST" });
  }

  static async moveStructureItem(itemId, newParentId) {

    return authenticatedRequest(`/api/admin/courses/structure/${itemId}/move`, {
      method: "PUT",
      body: JSON.stringify({ newParentId }),
    });
  }

  static async getStructureStats(courseId) {

    return authenticatedRequest(`/api/admin/courses/${courseId}/structure/stats`);
  }

  static async exportStructure(courseId, format = "json") {


    if (format === "json") {
      // JSON → usa helper normal (parsea JSON)
      return authenticatedRequest(`/api/admin/courses/${courseId}/structure/export?format=${format}`);
    }

    // CSV/XLSX → procesar como blob (no usar handleResponse aquí)
    const res = await authService.authenticatedRequest(
      `/api/admin/courses/${courseId}/structure/export?format=${format}`,
      { method: "GET" }
    );
    if (!res.ok) throw new Error(`Error exportando: ${res.status}`);
    const blob = await res.blob();

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `course-${courseId}-structure.${format}`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);


    return { message: "Archivo descargado exitosamente" };
  }

  static async importStructure(courseId, structureData, replaceExisting = false) {

    return authenticatedRequest(`/api/admin/courses/${courseId}/structure/import`, {
      method: "POST",
      body: JSON.stringify({ structure: structureData, replaceExisting }),
    });
  }

  static async validateStructure(courseId) {

    return authenticatedRequest(`/api/admin/courses/${courseId}/structure/validate`, { method: "POST" });
  }

  // ============================
  // Plantillas / Búsqueda / Historial (mock)
  // ============================
  static async getStructureTemplates() {

    return [
      {
        id: 1,
        name: "Curso Básico",
        description: "Estructura básica para cursos introductorios",
        structure: [
          {
            tipo: "nivel",
            nombre: "Introducción",
            descripcion: "Nivel introductorio del curso",
            orden: 1,
            children: [
              {
                tipo: "modulo",
                nombre: "Conceptos Básicos",
                descripcion: "Módulo de conceptos fundamentales",
                orden: 1,
              },
            ],
          },
        ],
      },
      {
        id: 2,
        name: "Curso Avanzado",
        description: "Estructura completa para cursos avanzados",
        structure: [
          { tipo: "nivel", nombre: "Fundamentos", descripcion: "Nivel de fundamentos", orden: 1 },
          { tipo: "nivel", nombre: "Práctica", descripcion: "Nivel práctico", orden: 2 },
        ],
      },
    ];
  }

  static async applyStructureTemplate(courseId, templateId) {

    const templates = await this.getStructureTemplates();
    const template = templates.find((t) => t.id === templateId);
    if (!template) throw new Error("Plantilla no encontrada");
    return this.importStructure(courseId, template.structure, false);
  }

  static async searchInStructure(courseId, searchTerm) {

    const structure = await this.getCourseStructure(courseId);

    const results = [];
    const walk = (items, path = []) => {
      items.forEach((item) => {
        const currentPath = [...path, item.nombre];
        if (
          item.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.descripcion?.toLowerCase().includes(searchTerm.toLowerCase())
        ) {
          results.push({ ...item, path: currentPath.join(" > "), level: currentPath.length - 1 });
        }
        if (Array.isArray(item.children) && item.children.length) walk(item.children, currentPath);
      });
    };
    walk(structure);

    return results;
  }

  static async getStructureHistory(courseId, { page = 1, limit = 20 } = {}) {

    const history = [
      { id: 1, action: "create", element: "Nivel 1", user: "Admin", timestamp: new Date().toISOString(), details: "Creado nuevo nivel" },
      { id: 2, action: "update", element: "Módulo A", user: "Admin", timestamp: new Date(Date.now() - 3600000).toISOString(), details: "Actualizada descripción" },
    ];
    return {
      history: history.slice((page - 1) * limit, page * limit),
      pagination: { page, limit, total: history.length, totalPages: Math.ceil(history.length / limit) },
    };
  }

  static async restoreStructureVersion(_courseId, _versionId) {

    return { message: "Versión restaurada exitosamente" };
  }

  // ============================
  // Recursos
  // ============================
  static async uploadStructureResources(estructuraId, files, isActive = true) {


    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    formData.append("activo", isActive ? "true" : "false");

    // Usar el wrapper (no definir Content-Type)
    const res = await authService.authenticatedRequest(
      `/api/admin/estructura/${estructuraId}/resources`,
      { method: "POST", body: formData, headers: { /* 'x-api-key' viene del wrapper */ } }
    );

    if (!res.ok) {
      const raw = await res.text().catch(() => "");
      if (res.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        window.location.href = "/login";
        throw new Error("Token expirado o inválido");
      }
      try {
        const j = raw ? JSON.parse(raw) : {};
        throw new Error(j?.message || j?.error || j?.details || `Error ${res.status}: ${res.statusText}`);
      } catch {
        throw new Error(raw || `Error ${res.status}: ${res.statusText}`);
      }
    }

    const ct = res.headers.get("content-type") || "";
    const data = ct.includes("application/json") ? await res.json() : await res.text();

    return data;
  }

  static async toggleResourceActivo(resourceId, isActive) {

    return authenticatedRequest(`/api/admin/resources/${resourceId}/activo`, {
      method: "PUT",
      body: JSON.stringify({ activo: isActive }),
    });
  }

  static async getStructureResources(estructuraId) {

    const data = await authenticatedRequest(`/api/admin/estructura/${estructuraId}/resources`);
    return data?.resources || data || [];
  }

  static async deleteModuleResource(resourceId) {

    return authenticatedRequest(`/api/admin/resources/${resourceId}`, { method: "DELETE" });
  }

  static async downloadModuleResource(moduleId, filename, originalName) {


    // Usa el wrapper y procesa blob
    const res = await authService.authenticatedRequest(
      `/api/admin/resources/${moduleId}/download/${filename}`,
      { method: "GET" }
    );
    if (!res.ok) {
      if (res.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        window.location.href = "/login";
        throw new Error("Token expirado o inválido");
      }
      throw new Error(`Error al descargar archivo: ${res.status}`);
    }

    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = originalName || filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);


    return true;
  }

  static async viewModuleResource(estructuraId, filename) {
    // Solo compone la URL (visualización directa)
    return `${API_BASE_URL}/api/admin/resources/${estructuraId}/view/${filename}`;
  }

  // ============================
  // Preguntas (estructura)
  // ============================
  static async createQuestion(questionData) {
    const {
      estructura_id,
      media_file,
      media_type,
      media_alt,
      media_caption,
      ...payload
    } = questionData;

    const url = `/api/admin/structure/${estructura_id}/questions`;

    try {
      // Con archivo → multipart
      if (media_file && typeof window !== "undefined") {
        const form = new FormData();
        form.append("pregunta", payload.pregunta || "");
        form.append("tipo_pregunta", payload.tipo_pregunta || "respuesta_corta");
        form.append("opciones", JSON.stringify(payload.opciones || {}));
        form.append("respuesta_correcta", JSON.stringify(payload.respuesta_correcta || {}));
        form.append("explicacion", payload.explicacion ?? "");
        form.append("nivel_dificultad", payload.nivel_dificultad || "medio");
        form.append("puntos", String(payload.puntos ?? 1));
        form.append("tiempo_limite_segundos", payload.tiempo_limite_segundos != null ? String(payload.tiempo_limite_segundos) : "");
        form.append("activo", String(payload.activo ?? true));
        form.append("ia", String(payload.ia ?? false));
        if (media_type) form.append("media_type", media_type);
        if (media_alt != null) form.append("media_alt", media_alt);
        if (media_caption != null) form.append("media_caption", media_caption);
        form.append("media_file", media_file, media_file.name);

        const res = await authService.authenticatedRequest(url, { method: "POST", body: form });
        if (!res.ok) {
          const txt = await res.text().catch(() => "");
          throw new Error(`HTTP ${res.status}${txt ? ` - ${txt}` : ""}`);
        }
        const ct = res.headers.get("content-type") || "";
        return ct.includes("application/json") ? res.json() : res.text();
      }

      // Sin archivo → JSON
      const res = await authService.authenticatedRequest(url, {
        method: "POST",
        body: JSON.stringify(payload),
        // headers opcionales extra; el wrapper ya pone x-api-key y content-type json
      });
      return handleResponse(res);
    } catch (error) {
      console.error("Error en CourseStructureService.createQuestion:", error);
      throw error;
    }
  }

  static async updateQuestion(questionId, data) {
    const {
      media_file,
      media_type,
      media_alt,
      media_caption,
      remove_media,
      ...payload
    } = data;

    const url = `/api/admin/questions/${questionId}`;

    // Con archivo → multipart
    if (media_file && typeof window !== "undefined") {
      const form = new FormData();
      Object.entries(payload).forEach(([k, v]) => {
        if (v === undefined || v === null) return;
        if (k === "opciones" || k === "respuesta_correcta") form.append(k, JSON.stringify(v));
        else form.append(k, String(v));
      });
      if (media_type) form.append("media_type", media_type);
      if (media_alt != null) form.append("media_alt", media_alt);
      if (media_caption != null) form.append("media_caption", media_caption);
      if (remove_media != null) form.append("remove_media", String(remove_media));
      form.append("media_file", media_file, media_file.name);

      const res = await authService.authenticatedRequest(url, { method: "PUT", body: form });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const ct = res.headers.get("content-type") || "";
      return ct.includes("application/json") ? res.json() : res.text();
    }

    // Sin archivo → JSON
    return authenticatedRequest(url, {
      method: "PUT",
      body: JSON.stringify({ ...payload, remove_media }),
    });
  }

  // ============================
// Juegos (estructura)
// ============================
static async createGame(gameData) {
  const {
    estructura_id,
    media_file,
    media_type,
    media_alt,
    media_caption,
    ...payload
  } = gameData;

  const url = `/api/admin/structure/${estructura_id}/games`;

  // Con archivo → multipart (como preguntas)
  if (media_file && typeof window !== "undefined") {
    const form = new FormData();
    form.append("tipo_juego", payload.tipo_juego); // ← requerido por la API
    form.append("configuracion", JSON.stringify(payload.configuracion ?? {}));
    if (payload.instrucciones != null) form.append("instrucciones", payload.instrucciones);
    if (payload.puntuacion_maxima != null) form.append("puntuacion_maxima", String(payload.puntuacion_maxima));
    if (payload.tiempo_limite_segundos != null) form.append("tiempo_limite_segundos", String(payload.tiempo_limite_segundos));
    form.append("activo", String(payload.activo ?? true));
    if (payload.ia != null) form.append("ia", String(payload.ia));

    if (media_type) form.append("media_type", media_type);          // "image"
    if (media_alt != null) form.append("media_alt", media_alt);
    if (media_caption != null) form.append("media_caption", media_caption);
    form.append("media_file", media_file, media_file.name);

    return authenticatedRequest(url, { method: "POST", body: form });
  }

  // Sin archivo → JSON normal
  return authenticatedRequest(url, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

  static async deleteQuestion(questionId) {

    return authenticatedRequest(`/api/admin/questions/${questionId}`, { method: "DELETE" });
  }

  static async deleteGame(gameId) {

    return authenticatedRequest(`/api/admin/games/${gameId}`, { method: "DELETE" });
  }

  static async updateGame(gameId, gameData) {
  const {
    media_file,
    media_type,
    media_alt,
    media_caption,
    remove_media,
    ...payload
  } = gameData;

  const url = `/api/admin/games/${gameId}`;

  // Con archivo → multipart (y puede venir remove_media)
  if (media_file && typeof window !== "undefined") {
    const form = new FormData();
    form.append("tipo_juego", payload.tipo_juego); // por consistencia
    form.append("configuracion", JSON.stringify(payload.configuracion ?? {}));
    if (payload.instrucciones != null) form.append("instrucciones", payload.instrucciones);
    if (payload.puntuacion_maxima != null) form.append("puntuacion_maxima", String(payload.puntuacion_maxima));
    if (payload.tiempo_limite_segundos != null) form.append("tiempo_limite_segundos", String(payload.tiempo_limite_segundos));
    form.append("activo", String(payload.activo ?? true));
    if (payload.ia != null) form.append("ia", String(payload.ia));
    if (remove_media != null) form.append("remove_media", String(remove_media));

    if (media_type) form.append("media_type", media_type);
    if (media_alt != null) form.append("media_alt", media_alt);
    if (media_caption != null) form.append("media_caption", media_caption);
    form.append("media_file", media_file, media_file.name);

    return authenticatedRequest(url, { method: "PUT", body: form });
  }

  // Sin archivo → JSON normal (incluye remove_media si quieres borrar la imagen)
  return authenticatedRequest(url, {
    method: "PUT",
    body: JSON.stringify({ ...payload, remove_media }),
  });
}
}

// Crear instancia del servicio
export const courseStructureService = CourseStructureService;
export default CourseStructureService;
